import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
  generateParticipationFundingDebit,
  getParticipationFundingDebitPreview,
  reverseParticipationFundingDebit,
} from 'services'
import { Button } from 'stories/components'
import { confirm } from 'utils'

const money = (v: any) =>
  `$${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

const today = () => new Date().toISOString().slice(0, 10)

interface PreviewItem {
  participantName: string
  amount: number
  bankName: string
  achReady: boolean
  achBlockedReason: string
}

/**
 * Review and draft the ACH debit for one funding event: pull each participant's funded share from
 * their bank account into the originator account. Mirror image of a participant payout. Shows who
 * is ready and who is blocked (no ACH / no banking on file) before drafting, reports what drafted
 * vs. was skipped afterwards, and can reverse a debit that was already generated.
 */
export function FundingDebitModal({
  funding,
  onClose,
  onDone,
}: {
  funding: { id: number; date?: string; amount?: number; achRunId?: number | null }
  onClose: () => void
  onDone: () => void
}) {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<any>(null)
  const [result, setResult] = useState<any>(null)
  const [effectiveDate, setEffectiveDate] = useState(funding.date || today())

  useEffect(() => {
    getParticipationFundingDebitPreview(funding.id)
      .then((res: any) => (res?.success ? setPreview(res) : toast(res?.message || 'Could not load preview.', { type: 'error' })))
      .catch(() => toast('Failed to load the debit preview.', { type: 'error' }))
      .finally(() => setLoading(false))
  }, [funding.id])

  const items: PreviewItem[] = preview?.items || []
  const ready = items.filter((i) => i.achReady)
  const blocked = items.filter((i) => !i.achReady)
  const alreadyDebited = Boolean(preview?.alreadyDebited)

  const onGenerate = async () => {
    setBusy(true)
    try {
      const res: any = await generateParticipationFundingDebit(funding.id, { effectiveDate })
      if (res?.success) {
        setResult(res)
        toast(`Funding debit drafted — ${res.count} participant(s), ${money(res.total)}.`, { type: 'success' })
        onDone()
      } else {
        toast(res?.message || 'No investors could be debited.', { type: 'error' })
      }
    } catch {
      toast('Failed to draft the funding debit.', { type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  const onReverse = async () => {
    const content = (
      <div className="mb-4 text-[18px]">
        Reverse this funding debit? The batch is marked reversed and the funding can be debited again.
      </div>
    )
    const ok = await confirm(content)
    if (!ok) return
    setBusy(true)
    try {
      const res: any = await reverseParticipationFundingDebit(funding.id)
      if (res?.success) {
        toast(res.message || 'Funding debit reversed.', { type: 'info' })
        onDone()
        onClose()
      } else {
        toast(res?.message || 'Could not reverse the debit.', { type: 'error' })
      }
    } catch {
      toast('Failed to reverse the funding debit.', { type: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-[760px] max-w-[95vw] flex-col rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-bold mb-1">Debit Investors — Funding {funding.date || ''}</h2>
        <p className="mb-3 text-sm text-gray-600">
          Draft an ACH debit that pulls each investor&apos;s funded share from their bank account. Investors
          without active ACH details are skipped with a reason.
        </p>

        {loading ? (
          <p className="py-10 text-center text-sm text-gray-400">Loading…</p>
        ) : result ? (
          <ResultView result={result} />
        ) : (
          <div className="min-h-0 flex-1 overflow-auto">
            <div className="mb-3 text-sm">
              <span className="font-semibold text-green-700">{ready.length}</span> ready ·{' '}
              <span className="font-semibold text-amber-600">{blocked.length}</span> blocked · total to draft{' '}
              <span className="font-semibold">{money(preview?.readyTotal)}</span>
            </div>

            {alreadyDebited && (
              <div className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                This funding has already been debited (Run #{preview?.achRunId}). Reverse it to draft again.
              </div>
            )}

            <table className="w-full text-sm text-left text-gray-900">
              <thead className="text-xs uppercase text-gray-700 bg-gray-100">
                <tr>
                  <th className="px-3 py-2">Investor</th>
                  <th className="px-3 py-2">Bank</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-4 text-center text-gray-500">
                      No allocations on this funding.
                    </td>
                  </tr>
                )}
                {items.map((it, idx) => (
                  <tr key={idx} className="border-b">
                    <td className="px-3 py-2">{it.participantName}</td>
                    <td className="px-3 py-2">{it.bankName || '—'}</td>
                    <td className="px-3 py-2 text-right">{money(it.amount)}</td>
                    <td className="px-3 py-2">
                      {it.achReady ? (
                        <span className="text-green-700">Ready</span>
                      ) : (
                        <span className="text-amber-600">{it.achBlockedReason}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {!alreadyDebited && (
              <div className="mt-4 flex items-center gap-2">
                <label className="text-sm text-gray-700">Effective date</label>
                <input
                  type="date"
                  value={effectiveDate}
                  onChange={(e) => setEffectiveDate(e.target.value)}
                  className="rounded border border-gray-300 px-2 py-1 text-sm"
                />
              </div>
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <Button color="gray" onClick={onClose} disabled={busy}>
            {result ? 'Close' : 'Cancel'}
          </Button>
          {!loading && !result && alreadyDebited && (
            <Button color="red" onClick={onReverse} loading={busy}>
              Reverse Debit
            </Button>
          )}
          {!loading && !result && !alreadyDebited && (
            <Button onClick={onGenerate} loading={busy} disabled={ready.length === 0}>
              {`Generate Debit (${ready.length})`}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

function ResultView({ result }: { result: any }) {
  const processed = result?.processed || []
  const skipped = result?.skipped || []
  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <p className="mb-3 text-sm text-gray-600">
        <span className="font-semibold text-green-700">{processed.length}</span> debited ·{' '}
        <span className="font-semibold text-amber-600">{skipped.length}</span> skipped · total{' '}
        <span className="font-semibold">{money(result?.total)}</span> · Run #{result?.runId}
      </p>
      {processed.length > 0 && (
        <table className="mb-4 w-full text-sm text-left text-gray-900">
          <thead className="text-xs uppercase text-gray-700 bg-gray-100">
            <tr>
              <th className="px-3 py-2">Investor</th>
              <th className="px-3 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody>
            {processed.map((p: any, idx: number) => (
              <tr key={idx} className="border-b">
                <td className="px-3 py-2">{p.name}</td>
                <td className="px-3 py-2 text-right">{money(p.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {skipped.length > 0 && (
        <table className="w-full text-sm text-left text-gray-900">
          <thead className="text-xs uppercase text-gray-700 bg-gray-100">
            <tr>
              <th className="px-3 py-2">Investor</th>
              <th className="px-3 py-2">Reason</th>
            </tr>
          </thead>
          <tbody>
            {skipped.map((s: any, idx: number) => (
              <tr key={idx} className="border-b">
                <td className="px-3 py-2">{s.name}</td>
                <td className="px-3 py-2 text-amber-600">{s.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}
